import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Slack, Send, Save, Check, X } from "lucide-react";

export default function AdminSlackSettings() {
  const [cfg, setCfg] = useState(null);
  const [webhook, setWebhook] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  async function load() {
    try {
      const r = await api.get("/admin/slack/config");
      setCfg(r.data);
      setEnabled(!!r.data.enabled);
      setPublicUrl(r.data.public_base_url || "");
    } catch { toast.error("Failed to load Slack config"); }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const payload = { enabled, public_base_url: publicUrl };
      if (webhook.trim()) payload.webhook_url = webhook.trim();
      await api.put("/admin/slack/config", payload);
      toast.success("Slack settings saved");
      setWebhook("");
      await load();
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  async function sendTest() {
    setTesting(true);
    try {
      const r = await api.post("/admin/slack/test");
      if (r.data.sent) toast.success("Test message posted to Slack");
      else toast.error(`Slack test failed: ${r.data.reason}`);
    } catch { toast.error("Slack test failed"); }
    finally { setTesting(false); }
  }

  return (
    <div className="min-h-screen bg-[#060606] text-white p-8" data-testid="admin-slack-page">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Slack className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.75} />
          <h1 className="font-display font-black text-3xl tracking-tight">SLACK DIGEST</h1>
        </div>
        <p className="text-sm text-[#8E9196] mb-6 max-w-2xl">
          Auto-post every Shift Briefing to a Slack channel with the CRIT count, top hotspot and a deep-link back to the full one-pager.
        </p>

        <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6 space-y-5">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Incoming webhook URL</div>
            <input
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              placeholder={cfg?.webhook_url_set ? "•••• already configured — paste a new one to replace" : "https://hooks.slack.com/services/T.../B.../..."}
              data-testid="slack-webhook-input"
              className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white font-mono placeholder:text-[#52525B]"
            />
            <div className="mt-1 text-[10px] font-mono text-[#52525B]">
              {cfg?.webhook_url_set ? (
                <span className="text-[#00F0FF] flex items-center gap-1"><Check className="w-3 h-3" /> Webhook currently set</span>
              ) : (
                <span className="flex items-center gap-1"><X className="w-3 h-3" /> No webhook yet — create one in Slack → Apps → Incoming Webhooks</span>
              )}
            </div>
          </div>

          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196] mb-1">Public base URL (for deep links in the digest)</div>
            <input
              value={publicUrl}
              onChange={(e) => setPublicUrl(e.target.value)}
              placeholder="https://forensiq.example.com"
              data-testid="slack-baseurl-input"
              className="w-full bg-black border border-white/10 focus:border-[#00F0FF] rounded-sm px-3 py-2 text-sm outline-none text-white font-mono placeholder:text-[#52525B]"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="slack-enabled-toggle">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="accent-[#00F0FF]" />
            <span className="text-sm">Enable auto-post on each new Shift Briefing</span>
          </label>

          <div className="flex gap-2 pt-2">
            <button onClick={save} disabled={saving}
                    data-testid="slack-save-btn"
                    className="flex items-center gap-2 px-4 py-2 bg-[#00F0FF] text-black text-[10px] font-mono uppercase tracking-widest rounded-sm hover:bg-white disabled:opacity-40">
              <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={sendTest} disabled={testing || !cfg?.webhook_url_set || !enabled}
                    data-testid="slack-test-btn"
                    className="flex items-center gap-2 px-4 py-2 border border-white/10 hover:border-[#00F0FF] text-[10px] font-mono uppercase tracking-widest text-[#8E9196] hover:text-white rounded-sm disabled:opacity-40">
              <Send className="w-3.5 h-3.5" /> {testing ? "Sending…" : "Send test"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
